import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import OverviewSectionEdit from './OverviewSectionEdit';
import LevelOneSectionEdit from './LevelOneSectionEdit';
import LevelTwoSectionEdit from './LevelTwoSectionEdit';
import LevelThreeSectionEdit from './LevelThreeSectionEdit';
import LevelFourSectionEdit from './LevelFourSectionEdit';
import LevelFiveSectionEdit from './LevelFiveSectionEdit';

import { getSongVideos } from '../../../services/videoService';

export default function SongDetailEdit({ song, musicals, selectedCharacter, onSave, onCancel, onError }) {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  
  const [formData, setFormData] = useState(() => {
    const baseData = {
      title: song?.title || '',
      musical_id: song?.musical_id || '',
      act_number: song?.act_number || 1,
      song_order: song?.song_order || null,
      key_signature: song?.key_signature || '',
      is_featured: song?.is_featured || false
    };

    // Add analysis data from selected character or defaults for new character
    if (selectedCharacter) {
      return {
        ...baseData,
        overview: selectedCharacter?.overview || {},
        level1: selectedCharacter?.level1 || '',
        level2: selectedCharacter?.level2 || '',
        level3: selectedCharacter?.level3 || [],
        level4: selectedCharacter?.level4 || {},
        level5: selectedCharacter?.level5 || {},
        phrases: selectedCharacter?.phrases || [],
        video_url_overview: selectedCharacter?.video_url_overview || '',
        video_url_level1: selectedCharacter?.video_url_level1 || '',
        video_url_level2: selectedCharacter?.video_url_level2 || '',
        video_url_level3: selectedCharacter?.video_url_level3 || '',
        video_url_level4: selectedCharacter?.video_url_level4 || '',
        video_url_level5: selectedCharacter?.video_url_level5 || ''
      };
    }

    return baseData;
  });

  // Update form data when selectedCharacter changes
  useEffect(() => {
    if (selectedCharacter) {
      setFormData(prev => ({
        ...prev,
        overview: selectedCharacter?.overview || {},
        level1: selectedCharacter?.level1 || '',
        level2: selectedCharacter?.level2 || '',
        level3: selectedCharacter?.level3 || [],
        level4: selectedCharacter?.level4 || {},
        level5: selectedCharacter?.level5 || {},
        phrases: selectedCharacter?.phrases || [],
        video_url_overview: selectedCharacter?.video_url_overview || '',
        video_url_level1: selectedCharacter?.video_url_level1 || '',
        video_url_level2: selectedCharacter?.video_url_level2 || '',
        video_url_level3: selectedCharacter?.video_url_level3 || '',
        video_url_level4: selectedCharacter?.video_url_level4 || '',
        video_url_level5: selectedCharacter?.video_url_level5 || ''
      }));
    }
  }, [selectedCharacter]);

  // Add state for video management
  const [sectionVideos, setSectionVideos] = useState({});
  const [loadingVideos, setLoadingVideos] = useState(false);

  // Add state for video playback modal
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState(null);
  const [currentVideoData, setCurrentVideoData] = useState(null);
  const [currentVideoKey, setCurrentVideoKey] = useState(null);

  // Add state for video upload
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  // Load existing videos for the character analysis
  useEffect(() => {
    const loadCharacterVideos = async () => {
      if (!selectedCharacter?.id) return;

      setLoadingVideos(true);
      try {
        const { data: videos, error } = await getSongVideos(song?.id);
        if (error) throw new Error(error);

        // Group videos by level and subsection
        const videosBySection = {};
        videos?.forEach(video => {
          // Only include videos for this character analysis
          if (video?.character_analysis_id === selectedCharacter?.id) {
            const key = `${video?.level}${video?.subsection ? `_${video?.subsection}` : ''}`;
            videosBySection[key] = video;
          }
        });

        setSectionVideos(videosBySection);
      } catch (error) {
        console.error('Error loading character videos:', error);
        onError?.('Failed to load videos');
      } finally {
        setLoadingVideos(false);
      }
    };

    loadCharacterVideos();
  }, [selectedCharacter?.id, song?.id, onError]);

  // Handle successful video upload
  const handleVideoUploadSuccess = (videoData) => {
    const key = `${videoData?.level}${videoData?.subsection ? `_${videoData?.subsection}` : ''}`;
    setSectionVideos(prev => ({
      ...prev,
      [key]: {
        ...videoData,
        character_analysis_id: selectedCharacter?.id
      }
    }));
  };

  // Handle video upload error
  const handleVideoUploadError = (error) => {
    onError?.(error || 'Failed to upload video');
  };

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Track per-level saved snapshots for Cancel support
  const [savedSnapshots, setSavedSnapshots] = useState({});
  const [levelSaving, setLevelSaving] = useState({});
  const [levelSaved, setLevelSaved] = useState({});

  // Compute unsaved changes per level by comparing formData to savedSnapshots
  const hasUnsavedChanges = React.useMemo(() => {
    const compare = (a, b) => JSON.stringify(a) !== JSON.stringify(b);
    return {
      overview: compare(formData?.overview, savedSnapshots?.overview?.data) || compare(formData?.video_url_overview || '', savedSnapshots?.overview?.videoUrl || ''),
      level1: compare(formData?.level1, savedSnapshots?.level1?.data) || compare(formData?.video_url_level1 || '', savedSnapshots?.level1?.videoUrl || ''),
      level2: compare(formData?.level2, savedSnapshots?.level2?.data) || compare(formData?.video_url_level2 || '', savedSnapshots?.level2?.videoUrl || ''),
      level3: compare(formData?.level3, savedSnapshots?.level3?.data) || compare(formData?.video_url_level3 || '', savedSnapshots?.level3?.videoUrl || ''),
      level4: compare(formData?.level4, savedSnapshots?.level4?.data) || compare(formData?.video_url_level4 || '', savedSnapshots?.level4?.videoUrl || ''),
      level5: compare(formData?.level5, savedSnapshots?.level5?.data) || compare(formData?.video_url_level5 || '', savedSnapshots?.level5?.videoUrl || ''),
    };
  }, [formData, savedSnapshots]);

  // Initialize snapshots when selectedCharacter loads
  useEffect(() => {
    if (selectedCharacter) {
      setSavedSnapshots({
        overview: { data: selectedCharacter?.overview || {}, videoUrl: selectedCharacter?.video_url_overview || '' },
        level1: { data: selectedCharacter?.level1 || '', videoUrl: selectedCharacter?.video_url_level1 || '' },
        level2: { data: selectedCharacter?.level2 || '', videoUrl: selectedCharacter?.video_url_level2 || '' },
        level3: { data: selectedCharacter?.level3 || [], videoUrl: selectedCharacter?.video_url_level3 || '' },
        level4: { data: selectedCharacter?.level4 || {}, videoUrl: selectedCharacter?.video_url_level4 || '' },
        level5: { data: selectedCharacter?.level5 || {}, videoUrl: selectedCharacter?.video_url_level5 || '' },
      });
    }
  }, [selectedCharacter?.id]);

  const handleSaveLevel = async (levelKey) => {
    setLevelSaving(prev => ({ ...prev, [levelKey]: true }));
    try {
      const levelDataMap = {
        overview: { data: formData?.overview, videoUrl: formData?.video_url_overview, saveKey: 'overview', videoKey: 'video_url_overview' },
        level1: { data: formData?.level1, videoUrl: formData?.video_url_level1, saveKey: 'level1', videoKey: 'video_url_level1' },
        level2: { data: formData?.level2, videoUrl: formData?.video_url_level2, saveKey: 'level2', videoKey: 'video_url_level2' },
        level3: { data: formData?.level3, videoUrl: formData?.video_url_level3, saveKey: 'level3', videoKey: 'video_url_level3' },
        level4: { data: formData?.level4, videoUrl: formData?.video_url_level4, saveKey: 'level4', videoKey: 'video_url_level4' },
        level5: { data: formData?.level5, videoUrl: formData?.video_url_level5, saveKey: 'level5', videoKey: 'video_url_level5' },
      };

      const levelInfo = levelDataMap?.[levelKey];
      if (!levelInfo) return;

      const saveData = {
        title: formData?.title,
        musical_id: formData?.musical_id,
        act_number: formData?.act_number,
        song_order: formData?.song_order,
        key_signature: formData?.key_signature,
        is_featured: formData?.is_featured,
        overview: formData?.overview,
        level1: formData?.level1,
        level2: formData?.level2,
        level3: formData?.level3,
        level4: formData?.level4,
        level5: formData?.level5,
        phrases: formData?.phrases,
        video_url_overview: formData?.video_url_overview || null,
        video_url_level1: formData?.video_url_level1 || null,
        video_url_level2: formData?.video_url_level2 || null,
        video_url_level3: formData?.video_url_level3 || null,
        video_url_level4: formData?.video_url_level4 || null,
        video_url_level5: formData?.video_url_level5 || null,
      };

      await onSave(saveData);

      // Update snapshot for this level
      setSavedSnapshots(prev => ({
        ...prev,
        [levelKey]: { data: levelInfo?.data, videoUrl: levelInfo?.videoUrl }
      }));

      setLevelSaved(prev => ({ ...prev, [levelKey]: true }));
      setTimeout(() => setLevelSaved(prev => ({ ...prev, [levelKey]: false })), 2000);
    } catch (error) {
      console.error('Level save error:', error);
      onError?.('Failed to save changes. Please try again.');
    } finally {
      setLevelSaving(prev => ({ ...prev, [levelKey]: false }));
    }
  };

  const handleCancelLevel = (levelKey) => {
    const snapshot = savedSnapshots?.[levelKey];
    if (!snapshot) return;

    const videoKeyMap = {
      overview: 'video_url_overview',
      level1: 'video_url_level1',
      level2: 'video_url_level2',
      level3: 'video_url_level3',
      level4: 'video_url_level4',
      level5: 'video_url_level5',
    };

    setFormData(prev => ({
      ...prev,
      [levelKey]: snapshot?.data,
      [videoKeyMap?.[levelKey]]: snapshot?.videoUrl,
    }));
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleOverviewChange = (data) => {
    setFormData(prev => ({
      ...prev,
      overview: data
    }));
  };

  const handleLevel1Change = (data) => {
    setFormData(prev => ({
      ...prev,
      level1: data
    }));
  };

  const handleLevel2Change = (data) => {
    setFormData(prev => ({
      ...prev,
      level2: data
    }));
  };

  const handleLevel3Change = (data) => {
    setFormData(prev => ({
      ...prev,
      level3: data
    }));
  };

  const handleLevel4Change = (data) => {
    setFormData(prev => ({
      ...prev,
      level4: data
    }));
  };

  const handleLevel5Change = (data) => {
    setFormData(prev => ({
      ...prev,
      level5: data
    }));
  };

  const handlePhrasesChange = (data) => {
    setFormData(prev => ({
      ...prev,
      phrases: data
    }));
  };

  const handleSave = async (e) => {
    // Always prevent default form submission
    if (e) {
      e?.preventDefault();
      e?.stopPropagation();
    }
    
    setIsSubmitting(true);
    setErrors({});
    
    try {
      // Validate required fields
      if (!formData?.title) {
        setErrors({ title: 'Song title is required' });
        return;
      }

      // Prepare the data for saving
      const saveData = {
        title: formData?.title,
        musical_id: formData?.musical_id,
        act_number: formData?.act_number,
        song_order: formData?.song_order,
        key_signature: formData?.key_signature,
        is_featured: formData?.is_featured,
        
        // Character-specific analysis data
        overview: formData?.overview,
        level1: formData?.level1,
        level2: formData?.level2,
        level3: formData?.level3,
        level4: formData?.level4,
        level5: formData?.level5,
        phrases: formData?.phrases,

        // Per-level Mux video playback IDs
        video_url_overview: formData?.video_url_overview || null,
        video_url_level1: formData?.video_url_level1 || null,
        video_url_level2: formData?.video_url_level2 || null,
        video_url_level3: formData?.video_url_level3 || null,
        video_url_level4: formData?.video_url_level4 || null,
        video_url_level5: formData?.video_url_level5 || null
      };

      await onSave(saveData);
    } catch (error) {
      console.error('Save error:', error);
      onError?.('Failed to save changes. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Tab navigation data
  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'FileText' },
    { id: 'level1', label: 'Level 1', icon: 'Music' },
    { id: 'level2', label: 'Level 2', icon: 'Music2' },
    { id: 'level3', label: 'Level 3', icon: 'Music3' },
    { id: 'level4', label: 'Level 4', icon: 'Music4' },
    { id: 'level5', label: 'Level 5', icon: 'Star' }
  ];

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header with character info */}
      {selectedCharacter && (
        <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl shadow-2xl mb-6">
          <div className="px-6 py-4 border-b border-white border-opacity-20">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-heading font-bold text-white mb-2">
                    Editing for Character: {selectedCharacter?.role?.character_name}
                  </h3>
                  <p className="text-blue-100 font-body">
                    {selectedCharacter?.role?.voice_type && `${selectedCharacter?.role?.voice_type?.replace('_', ' ')} • `}
                    {selectedCharacter?.role?.role_type}
                    {selectedCharacter?.role?.musicals?.title && ` • ${selectedCharacter?.role?.musicals?.title}`}
                  </p>
                </div>
                <div className="text-sm text-blue-100 font-caption">
                  Character-specific analysis
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl mb-6 overflow-hidden">
        <div className="flex flex-wrap border-b border-white border-opacity-20">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              type="button"
              onClick={() => setActiveTab(tab?.id)}
              className={`
                flex-1 min-w-[120px] px-4 py-4 font-medium text-sm transition-all duration-200
                flex items-center justify-center space-x-2
                ${activeTab === tab?.id
                  ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white border-b-2 border-white' :'text-blue-200 hover:bg-white hover:bg-opacity-10 hover:text-white'
                }
              `}
            >
              <Icon name={tab?.icon} size={18} />
              <span>{tab?.label}</span>
              {hasUnsavedChanges?.[tab?.id] && (
                <span className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0" title="Unsaved changes" />
              )}
            </button>
          ))}
        </div>
      </div>

      <form onSubmit={handleSave} className="space-y-6">
        {/* Tab Content */}
        {activeTab === 'overview' && (
          <OverviewSectionEdit
            data={formData?.overview || {}}
            onChange={handleOverviewChange}
            errors={errors}
            song={song}
            selectedCharacter={selectedCharacter}
            formData={formData}
            handleInputChange={handleInputChange}
            videoUrl={formData?.video_url_overview || ''}
            onVideoUrlChange={(val) => setFormData(prev => ({ ...prev, video_url_overview: val }))}
            handleSaveLevel={handleSaveLevel}
            handleCancelLevel={handleCancelLevel}
            savedSnapshots={savedSnapshots}
            levelSaving={levelSaving}
            levelSaved={levelSaved}
            hasUnsavedChanges={hasUnsavedChanges?.overview}
          />
        )}

        {activeTab === 'level1' && (
          <LevelOneSectionEdit
            data={formData?.level1 || ''}
            onChange={handleLevel1Change}
            errors={errors}
            videoUrl={formData?.video_url_level1 || ''}
            onVideoUrlChange={(val) => setFormData(prev => ({ ...prev, video_url_level1: val }))}
            handleSaveLevel={handleSaveLevel}
            handleCancelLevel={handleCancelLevel}
            savedSnapshots={savedSnapshots}
            levelSaving={levelSaving}
            levelSaved={levelSaved}
            hasUnsavedChanges={hasUnsavedChanges?.level1}
          />
        )}

        {activeTab === 'level2' && (
          <LevelTwoSectionEdit
            data={formData?.level2 || ''}
            onChange={handleLevel2Change}
            errors={errors}
            videoUrl={formData?.video_url_level2 || ''}
            onVideoUrlChange={(val) => setFormData(prev => ({ ...prev, video_url_level2: val }))}
            handleSaveLevel={handleSaveLevel}
            handleCancelLevel={handleCancelLevel}
            savedSnapshots={savedSnapshots}
            levelSaving={levelSaving}
            levelSaved={levelSaved}
            hasUnsavedChanges={hasUnsavedChanges?.level2}
          />
        )}

        {activeTab === 'level3' && (
          <LevelThreeSectionEdit
            data={formData?.level3 || []}
            onChange={handleLevel3Change}
            errors={errors}
            songId={song?.id}
            roleId={selectedCharacter?.role?.id}
            videoUrl={formData?.video_url_level3 || ''}
            onVideoUrlChange={(val) => setFormData(prev => ({ ...prev, video_url_level3: val }))}
            handleSaveLevel={handleSaveLevel}
            handleCancelLevel={handleCancelLevel}
            savedSnapshots={savedSnapshots}
            levelSaving={levelSaving}
            levelSaved={levelSaved}
            hasUnsavedChanges={hasUnsavedChanges?.level3}
          />
        )}

        {activeTab === 'level4' && (
          <LevelFourSectionEdit
            data={formData?.level4 || {}}
            onChange={handleLevel4Change}
            errors={errors}
            songId={song?.id}
            videoUrl={formData?.video_url_level4 || ''}
            onVideoUrlChange={(val) => setFormData(prev => ({ ...prev, video_url_level4: val }))}
            handleSaveLevel={handleSaveLevel}
            handleCancelLevel={handleCancelLevel}
            savedSnapshots={savedSnapshots}
            levelSaving={levelSaving}
            levelSaved={levelSaved}
            hasUnsavedChanges={hasUnsavedChanges?.level4}
          />
        )}

        {activeTab === 'level5' && (
          <LevelFiveSectionEdit
            data={formData?.level5 || {}}
            phrases={formData?.phrases || []}
            onChange={handleLevel5Change}
            onPhrasesChange={handlePhrasesChange}
            errors={errors}
            videoUrl={formData?.video_url_level5 || ''}
            onVideoUrlChange={(val) => setFormData(prev => ({ ...prev, video_url_level5: val }))}
            handleSaveLevel={handleSaveLevel}
            handleCancelLevel={handleCancelLevel}
            savedSnapshots={savedSnapshots}
            levelSaving={levelSaving}
            levelSaved={levelSaved}
            hasUnsavedChanges={hasUnsavedChanges?.level5}
          />
        )}

        {/* Save Changes Button - Sticky at bottom */}
        <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6 sticky bottom-6 z-10">
          <div className="flex justify-end items-center space-x-4">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              className="border-white text-white hover:bg-white hover:text-indigo-900"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              iconName="Save"
              iconPosition="left"
              className={`
                bg-white text-indigo-900 hover:bg-gray-100
                ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}
              `}
            >
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}